import { ProductDocsWidget } from '../../widgets/ProductDocsWidget';
import * as React from 'react';
import { CrumbWidget } from '../../widgets/CrumbWidget';
import { ContentWidget } from '../../widgets/ContentWidget';
import { PageWidget } from '../../widgets/layout/PageWidget';
import { SectionWidget } from '../../widgets/layout/SectionWidget';
import styled from '@emotion/styled';
import { ReactorBlurbWidget } from '../_shared/ReactorBlurbWidget';

const oxide = require('../../../media/stack/oxide.png');

const img_deployments = require('../../../media/oxide/deployments.jpg');
const img_schema = require('../../../media/oxide/schema.jpg');
const img_view_editing = require('../../../media/oxide/view-editing.jpg');
const img_coediting = require('../../../media/oxide/co-editing.jpg');
const img_cmdpalette = require('../../../media/oxide/cmdpalette.jpg');
const img_workspaces = require('../../../media/oxide/workspaces.jpg');

const img_theme_1 = require('../../../media/oxide/themes/1.jpeg');
const img_theme_2 = require('../../../media/oxide/themes/2.jpeg');
const img_theme_3 = require('../../../media/oxide/themes/3.jpeg');
const img_theme_4 = require('../../../media/oxide/themes/4.jpeg');
const img_theme_5 = require('../../../media/oxide/themes/5.jpeg');

export const OxidePage: React.FC = () => {
  return (
    <PageWidget width={1100} title="OXIDE">
      <ContentWidget>
        <h1>
          <CrumbWidget parts={['Platform stack', 'OXIDE']} />
        </h1>
        <p>
          OXIDE is the web-based development environment for JourneyApps Platform projects. Build application
          interfaces, define data models, and write CloudCode tasks for automation and integrations in one workspace.
        </p>
        <img src={oxide} />
        <p>
          Work directly in code or use visual tools to edit views and schemas. Preview changes as you build, collaborate
          with other developers, manage source control, and deploy your project to testing, staging, and production.
        </p>
        <SectionWidget
          index={1}
          image={img_view_editing}
          label="View editing"
          content={`
      Edit views visually with drag & drop support and click-to-add component functionality. Code is automatically
      written as components are added, and the view structures are represented as easy-to-use trees.
      `}
        />
        <SectionWidget
          index={2}
          image={img_schema}
          ltr={false}
          label="Schema"
          content={`
      Edit schemas visually with full XML and WYSIWYG interoperability.
      As you work in XML, the visual preview updates dynamically, with the same true for editing the graph directly.
      Easily visualize relationships, make connections and wrangle complex schemas as applications grow in complexity.
      `}
        />
        <SectionWidget
          index={3}
          image={img_deployments}
          label="Deployments"
          content={`
      OXIDE allows you to view deployment invocation history with full deployment logs
      that include live logs as a deployment is in progress. Compare logs between builds and go straight to the original line in the source
      code with deep linking when errors occur.
      `}
        />
        <h2>Even more advanced features</h2>
        <ul>
          <li>IntelliSense and advanced code analysis.</li>
          <li>Action shortcut editing for common actions and code editing.</li>
          <li>Right click & drag-and-drop support everywhere.</li>
          <li>Advanced conflict resolution complete with co-editing support.</li>
        </ul>
        <SectionWidget
          index={1}
          image={img_coediting}
          label="Live co-editing"
          content={`
Edit files with other developers at the same time using our co-editing feature. Share drafts, work on resolving conflicts together
and see changes from other developers in real-time.
      `}
        />
        <SectionWidget
          index={2}
          ltr={false}
          image={img_cmdpalette}
          label="Command palette"
          content={`
Find and run actions from the command palette. Open panels, change settings, and control the editor
without navigating through menus.
      `}
        />
        <SectionWidget
          index={3}
          image={img_workspaces}
          label="Advanced workspaces"
          content={`
Arrange your workspace around the task at hand, with code, previews, data, and logs side by side.
Powered by Reactor, OXIDE lets you rearrange panels, move them between browser windows, and dock or minimize them in trays.
      `}
        />
        <S.ThemesContent>
          <h1>Themes</h1>
          <p>OXIDE supports multiple themes including the ability to upload VSCode editor themes directly.</p>
          <S.Themes>
            <S.Theme src={img_theme_1} />
            <S.Theme src={img_theme_2} />
            <S.Theme src={img_theme_3} />
            <S.Theme src={img_theme_4} />
            <S.Theme src={img_theme_5} />
          </S.Themes>
        </S.ThemesContent>
        <ReactorBlurbWidget name="OXIDE" />
      </ContentWidget>
      <ProductDocsWidget
        title="Start building in OXIDE"
        description="Get familiar with your workspace, manage your source, and deploy your project."
        topics={['OXIDE']}
      />
    </PageWidget>
  );
};

namespace S {
  export const ThemesContent = styled(ContentWidget)`
    margin-top: 30px;
    margin-bottom: 30px;
  `;

  export const Themes = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    column-gap: 20px;
    row-gap: 20px;
    margin-top: 10px;
  `;

  export const Theme = styled.img`
    max-width: 200px !important;
    margin: inherit !important;
  `;
}
